import { type fabric } from 'fabric';
import type Canvas from '../objects/Canvas';
import Shape from '../objects/Shape';
import { DEFAULT_FONT_SIZE } from '../objects/EmbeddedTextBehaviorsV2';
import ShapeAnnotation from '../shapes/ShapeAnnotation';
import StylusShape from './StylusShape';
import { type IObjectOptions, type IPoint } from 'fabric/fabric-impl';
import { type MouseEventData } from '../external-types';
import { type RecordId } from '../types';
import type CollaborationBehaviors from '../objects/CollaborationBehaviors';
import type Stylus from './Stylus';
import DraggableBrush from './DraggableBrush';
import { isShape } from '../objects/guards';

const CLICK_TIMEOUT = 200;
const CLICK_RADIUS = 20;

interface ClickStart {
  started: number;
  pointer: IPoint;
}

type DrawnObject = fabric.Object & CollaborationBehaviors;

export type StylusShapeOptions = Pick<
  Shape,
  'fill' | 'iconName' | 'variant' | 'recordType'
>;

export interface CreateObjectOptions {
  left: number;
  top: number;
  shapeName?: string;
  authorId: RecordId | null;
}

// Brush for drawing shape objects.
class ShapeDrawingBrush<
  O extends Record<string, any> = StylusShapeOptions
> extends DraggableBrush {
  private clickStart: ClickStart | undefined;
  private dragHandle: string | null;
  protected shape: DrawnObject | null;

  constructor(
    public readonly canvas: Canvas,
    private readonly stylus: Stylus,
    protected readonly initialOptions: Partial<O> = {}
  ) {
    super();
    this.shape = null;
    this.dragHandle = 'br';
  }

  objectInProgress() {
    return this.shape;
  }

  async startDragging() {
    this.shape = this.createShape({
      drawing: false,
      clickStart: this.clickStart,
    });
    return this.objectInProgress();
  }

  onMouseDown(pointer: IPoint, eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return;
    }

    this.shape = null;
    this.clickStart = {
      started: Date.now(),
      pointer,
    };
    this.canvas.controller.actionSetStylusMenu(null);
  }

  onMouseMove(pointer: IPoint, eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return;
    }

    if (!this.shape) {
      const clickRadiusForZoom = CLICK_RADIUS / this.canvas.getZoom();

      // See if we should start making a dynamic shape.
      if (
        this.clickStart &&
        (Date.now() > this.clickStart.started + CLICK_TIMEOUT ||
          Math.abs(pointer.x - this.clickStart.pointer.x) >
            clickRadiusForZoom ||
          Math.abs(pointer.y - this.clickStart.pointer.y) > clickRadiusForZoom)
      ) {
        if (pointer.x < this.clickStart.pointer.x) {
          if (pointer.y < this.clickStart.pointer.y) {
            this.dragHandle = 'tl';
          } else {
            this.dragHandle = 'bl';
          }
        } else {
          if (pointer.y < this.clickStart.pointer.y) {
            this.dragHandle = 'tr';
          } else {
            this.dragHandle = 'br';
          }
        }

        const shape = this.createShape({
          drawing: true,
          eventData,
          clickStart: this.clickStart,
        });
        shape.fire('scaling');
      }
    } else {
      this.canvas._transformObject(eventData.e);
    }
  }

  onMouseUp(eventData: MouseEventData) {
    if (!this.canvas._isMainEvent(eventData.e)) {
      return true;
    }

    const dragged = !!this.shape;

    // if this.shape doesn't exist, the user just clicked rather than dragging. Create a default shape.
    const shape =
      this.shape ??
      this.createShape({ drawing: false, clickStart: this.clickStart });

    if (dragged) {
      // Finalize the drag.
      this.canvas._finalizeCurrentTransform(eventData.e);
      this.canvas._currentTransform = null;
    }

    // We manually call canvas._transformObject() in onMouseMove(), but this may
    // not actually get scaled. So, ensure that 'scaled' always fires.
    shape.fire('scaled');
    this.stylus.drawingComplete();

    const finishRender = () => {
      this.canvas.controller.actionShowObjectToolbar();
      this.canvas.requestRenderAll();
    };

    // For text shapes, start editing once the shape is full constructed.
    if (
      isShape(shape) &&
      (shape.shapeName === 'text' || shape.shapeName === 'note')
    ) {
      window.setTimeout(() => {
        void shape.startTextEditing().then(finishRender);
      });
    } else {
      this.canvas.controller.undoManager.completeUndoGroup();
      finishRender();
    }

    return false;
  }

  _createObject(options: CreateObjectOptions, drawing: boolean): DrawnObject {
    if (!(this.stylus instanceof StylusShape)) {
      throw new Error('Incorrect stylus type provided');
    }

    const controller = this.canvas.controller;
    const shapeOptions = {
      controller,
      ...controller.defaultsManager.for('shape', this.stylus.shapeName),
      ...this.initialOptions,
      ...options,
    };

    if (drawing) {
      if (this.stylus.shapeName === 'text') {
        shapeOptions.wrapText = true;
        this.dragHandle = 'mr';
        shapeOptions.height = 30;
      } else if (this.stylus.shapeName === 'annotation') {
        // When drawing an annotation the click point should be the tail and
        // start dragging the body of the shape.
        this.dragHandle = null;
        shapeOptions.tailRelativePosX = 0;
        shapeOptions.tailRelativePosY = ShapeAnnotation.defaultHeight() / 2;
        shapeOptions.top = options.top - ShapeAnnotation.defaultHeight() / 2;
      } else if (this.stylus.shapeName === 'progressBar') {
        this.dragHandle = 'mr';
        shapeOptions.height = 20;
      } else {
        shapeOptions.scaleX = 0.05;
        shapeOptions.scaleY = 0.05;
      }
    } else {
      if (this.stylus.shapeName === 'text') {
        // The scale depends only on the fontSize for autoGrow text boxes so
        // make sure it is set here.
        const newScale =
          (shapeOptions.fontSize || DEFAULT_FONT_SIZE) / DEFAULT_FONT_SIZE;
        shapeOptions.scaleX = newScale;
        shapeOptions.scaleY = newScale;
      } else if (this.stylus.shapeName === 'annotation') {
        shapeOptions.left = options.left + 40;
        shapeOptions.top = options.top - ShapeAnnotation.defaultHeight() / 2;
      }
    }

    return Shape.create(shapeOptions);
  }

  public createShape(
    opts: (
      | {
          drawing: false;
        }
      | {
          eventData: MouseEventData;
          drawing: true;
        }
    ) & { clickStart?: ClickStart; options?: IObjectOptions }
  ) {
    const { clickStart, options = {} } = opts;
    const shape = this._createObject(
      {
        shapeName:
          this.stylus instanceof StylusShape
            ? this.stylus.shapeName
            : undefined,
        authorId: this.canvas.controller.state.userId,
        ...options,
        left: options.left ?? clickStart?.pointer.x ?? 0,
        top: options.top ?? clickStart?.pointer.y ?? 0,
      },
      opts.drawing
    );
    this.shape = shape;
    this.canvas.setActiveObject(this.shape);
    // Hide toolbar we showed when setting the active object.
    this.canvas.controller.actionHideObjectToolbar();

    if (opts.drawing) {
      this._setupTransform(opts.eventData, this.shape);
    }

    return shape;
  }

  _setupTransform({ e }: MouseEventData, shape: fabric.Object) {
    shape.__corner = this.dragHandle;
    this.canvas._setupCurrentTransform(e, shape, true);
  }

  _render() {}
}

export default ShapeDrawingBrush;
